import { supabase } from '../lib/supabase';

/**
 * Email Notification Service
 * Handles all email notification operations including sending welcome emails
 */

const SUPABASE_FUNCTION_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-welcome-email`;

/**
 * Send welcome email to new user
 * @param {Object} userData - User data including email and name
 * @returns {Promise<Object>} Result of email send operation
 */
export const sendUserWelcomeEmail = async (userData) => {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();

    if (!session) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(SUPABASE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({
        emailType: 'user_welcome',
        recipientEmail: userData?.email,
        userName: `${userData?.first_name} ${userData?.last_name}`?.trim()
      })
    });

    const result = await response?.json();

    if (!response?.ok) {
      throw new Error(result.error || 'Failed to send welcome email');
    }

    // Update email notification status in database
    if (result?.messageId) {
      await supabase?.from('email_notifications')?.update({
        status: 'sent',
        resend_message_id: result?.messageId
      })?.eq('user_id', userData?.id)?.eq('email_type', 'user_welcome')?.eq('status', 'pending');
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Error sending user welcome email:', error);

    // Log error to database
    try {
      await supabase?.from('email_notifications')?.update({
        status: 'failed',
        error_message: error?.message
      })?.eq('user_id', userData?.id)?.eq('email_type', 'user_welcome')?.eq('status', 'pending');
    } catch (dbError) {
      console.error('Error updating email notification status:', dbError);
    }

    return { success: false, error: error?.message };
  }
};

/**
 * Send welcome email to new seller
 * @param {Object} sellerData - Seller account data including business email and name
 * @returns {Promise<Object>} Result of email send operation
 */
export const sendSellerWelcomeEmail = async (sellerData) => {
  try {
    const { data: { session } } = await supabase?.auth?.getSession();

    if (!session) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(SUPABASE_FUNCTION_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({
        emailType: 'seller_welcome',
        recipientEmail: sellerData?.business_email,
        businessName: sellerData?.business_name,
        tier: sellerData?.tier
      })
    });

    const result = await response?.json();

    if (!response?.ok) {
      throw new Error(result.error || 'Failed to send seller welcome email');
    }

    // Update email notification status in database
    if (result?.messageId) {
      await supabase?.from('email_notifications')?.update({
        status: 'sent',
        resend_message_id: result?.messageId
      })?.eq('seller_account_id', sellerData?.id)?.eq('email_type', 'seller_welcome')?.eq('status', 'pending');
    }

    return { success: true, data: result };
  } catch (error) {
    console.error('Error sending seller welcome email:', error);

    // Log error to database
    try {
      await supabase?.from('email_notifications')?.update({
        status: 'failed',
        error_message: error?.message
      })?.eq('seller_account_id', sellerData?.id)?.eq('email_type', 'seller_welcome')?.eq('status', 'pending');
    } catch (dbError) {
      console.error('Error updating email notification status:', dbError);
    }

    return { success: false, error: error?.message };
  }
};

/**
 * Send newsletter subscription confirmation email
 * @param {string} email - Subscriber email address
 * @param {string} userName - Optional subscriber name
 * @returns {Promise<Object>} Response from email service
 */
export const sendNewsletterConfirmation = async (email, userName = null) => {
  try {
    // Call Supabase edge function to send newsletter confirmation
    const { data, error } = await supabase?.functions?.invoke('send-newsletter-confirmation', {
      body: {
        email,
        userName
      }
    });

    if (error) {
      console.error('Error invoking send-newsletter-confirmation function:', error);
      throw new Error(error?.message || 'Failed to send newsletter confirmation email');
    }

    // Update email notification status to 'sent'
    if (data?.messageId) {
      const { error: updateError } = await supabase?.from('email_notifications')?.update({
        status: 'sent',
        sent_at: new Date()?.toISOString(),
        resend_message_id: data?.messageId
      })?.eq('recipient_email', email)?.eq('email_type', 'newsletter_subscription')?.eq('status', 'pending')?.order('created_at', { ascending: false })?.limit(1);

      if (updateError) {
        console.warn('Failed to update email notification status:', updateError);
      }
    }

    return {
      success: true,
      message: 'Newsletter confirmation email sent successfully',
      messageId: data?.messageId
    };
  } catch (err) {
    console.error('Error in sendNewsletterConfirmation:', err);

    // Update email notification status to 'failed'
    try {
      await supabase?.from('email_notifications')?.update({
        status: 'failed',
        error_message: err?.message || 'Unknown error'
      })?.eq('recipient_email', email)?.eq('email_type', 'newsletter_subscription')?.eq('status', 'pending')?.order('created_at', { ascending: false })?.limit(1);
    } catch (updateErr) {
      console.warn('Failed to update email notification status:', updateErr);
    }

    throw err;
  }
};

/**
 * Get email notification history for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} List of email notifications
 */
export const getUserEmailNotifications = async (userId) => {
  try {
    const { data, error } = await supabase?.from('email_notifications')?.select('*')?.eq('user_id', userId)?.order('created_at', { ascending: false });

    if (error) throw error;

    return { success: true, data };
  } catch (error) {
    console.error('Error fetching email notifications:', error);
    return { success: false, error: error?.message };
  }
};

/**
 * Retry failed email notifications
 * @param {string} notificationId - Email notification ID
 * @returns {Promise<Object>} Result of retry operation
 */
export const retryFailedEmail = async (notificationId) => {
  try {
    // Get notification details
    const { data: notification, error: fetchError } = await supabase?.from('email_notifications')?.select('*')?.eq('id', notificationId)?.single();

    if (fetchError) throw fetchError;

    if (notification?.email_type === 'user_welcome') {
      return await sendUserWelcomeEmail({
        id: notification?.user_id,
        email: notification?.recipient_email,
        first_name: '',
        last_name: ''
      });
    } else if (notification?.email_type === 'seller_welcome') {
      return await sendSellerWelcomeEmail({
        id: notification?.seller_account_id,
        business_email: notification?.recipient_email,
        business_name: '',
        tier: 'entry'
      });
    }

    throw new Error('Unknown email type');
  } catch (error) {
    console.error('Error retrying failed email:', error);
    return { success: false, error: error?.message };
  }
};

export default {
  sendUserWelcomeEmail,
  sendSellerWelcomeEmail,
  sendNewsletterConfirmation,
  getUserEmailNotifications,
  retryFailedEmail
};